import React, { useState, useEffect } from "react";
import { message, Popover } from "antd";
import { Context, Hooks, Util } from "@xt/xtbase";
import styles from "./index.module.less";
import giftIcon from "assets/images/homeNew/gift.png";
import taskHeroImg from "assets/images/home/task.png";
import { getActivityList, claimActivityReward } from "@/api/activity";
import {
  isHomeActivityCandidate,
  pickPublishedFromList,
  buildStepsFromRewards,
  getCompletedCount,
  getCurrentStepIndex,
  getJumpUrl,
  appendActivityDepositQueryToPath,
} from "../activityHomeShared";
import { resolveActivityWebJumpPath } from "@/utils/activityDepositJump";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getUrl } = Util;

function LimitedTimeIcon() {
  return (
    <span className={styles.limitedIcon} aria-hidden>
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M2.54578 9.1667C2.93006 5.69033 5.69033 2.93006 9.1667 2.54578V4.22574C6.61193 4.59116 4.59116 6.61193 4.22574 9.1667H2.54578Z" fill="#8D95A2" />
        <path d="M10.8334 2.54578V4.22575C13.3881 4.59119 15.4089 6.61195 15.7743 9.1667H17.4542C17.07 5.69035 14.3097 2.93008 10.8334 2.54578Z" fill="#8D95A2" />
        <path d="M9.1667 15.7743C6.61195 15.4089 4.59119 13.3881 4.22575 10.8334H2.54578C2.93008 14.3097 5.69035 17.07 9.1667 17.4542V15.7743Z" fill="#8D95A2" />
        <path fillRule="evenodd" clipRule="evenodd" d="M14.1667 10C14.1667 12.3012 12.3012 14.1667 10 14.1667C7.69881 14.1667 5.83333 12.3012 5.83333 10C5.83333 7.69881 7.69881 5.83333 10 5.83333C12.3012 5.83333 14.1667 7.69881 14.1667 10ZM9.375 7.21463V10.5479L11.1427 12.3157L12.0266 11.4318L10.625 10.0303V7.21463H9.375Z" fill="#8D95A2" />
        <path d="M15.7743 10.8333C15.4088 13.3881 13.3881 15.4088 10.8334 15.7742V17.4542C14.3097 17.0699 17.0699 14.3097 17.4542 10.8333H15.7743Z" fill="#B9F78A" />
      </svg>
    </span>
  );
}

/**
 * 首页任务中心：SSR 下发 initialActivity + 登录后 CSR 校准（静默失败保留 SSR）
 */
export default function TaskCenter({ initialActivity = null, activityRefreshRef }) {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const loginStatus = appState?.loginStatus;
  const userInfo = appState?.userInfo ?? null;

  const [activity, setActivity] = useState(() =>
    isHomeActivityCandidate(initialActivity) ? initialActivity : null
  );
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const depositNavigatingRef = React.useRef(false);

  React.useEffect(() => {
    if (isHomeActivityCandidate(initialActivity)) {
      setActivity(initialActivity);
    } else if (initialActivity != null) {
      setActivity(null);
    }
  }, [initialActivity]);

  const fetchActivity = React.useCallback(async ({ silent = false } = {}) => {
    try {
      const list = await getActivityList();
      const picked = pickPublishedFromList(list);
      setActivity((prev) => {
        if (picked) return picked;
        if (silent && prev) return prev;
        return null;
      });
    } catch (e) {
      console.error("[TaskCenter] getActivityList failed", e);
      setActivity((prev) => {
        if (silent && prev) return prev;
        return null;
      });
    }
  }, []);

  const fetchActivityRef = React.useRef(fetchActivity);
  fetchActivityRef.current = fetchActivity;
  const activityRef = React.useRef(activity);
  activityRef.current = activity;

  /** 供顶部余额轮询：仅有首页任务数据时静默刷新活动接口 */
  React.useEffect(() => {
    if (!activityRefreshRef) return undefined;
    activityRefreshRef.current = () => {
      if (!activityRef.current) return;
      fetchActivityRef.current({ silent: true });
    };
    return () => {
      activityRefreshRef.current = () => {};
    };
  }, [activityRefreshRef]);

  useEffect(() => {
    if (loginStatus === 2) {
      setActivity(null);
      setClaimed(false);
    }
  }, [loginStatus]);

  /** 已登录且灰度通过：拉接口；有 SSR 数据时静默校准，避免空结果冲掉首屏 */
  useEffect(() => {
    if (loginStatus !== 1) return;
    fetchActivity({ silent: !!initialActivity });
  }, [loginStatus, initialActivity, fetchActivity]);

  const steps = React.useMemo(() => {
    if (!activity) return [];
    return buildStepsFromRewards(activity.rewards, t);
  }, [activity, t]);

  const completedCount = getCompletedCount(steps);
  const stepCount = steps.length;
  const currentStep = getCurrentStepIndex(steps, completedCount);
  const isCompleted = activity?.userStatus === "COMPLETED";
  const currentStepObj = steps[currentStep - 1] ?? null;
  const canClaim = stepCount > 0 && completedCount >= stepCount && !claimed;

  const claimRewardRecordId = (() => {
    const rewardIndex = currentStepObj?.rewardIndex;
    if (rewardIndex == null) return null;
    const reward = activity?.rewards?.[rewardIndex];
    return reward?.userRewardRecordId ?? null;
  })();

  const firstReward = activity?.rewards?.[0];
  const rewardAmount = firstReward?.rewardAmount;
  const rewardCurrency = firstReward?.rewardCurrency ?? "";
  const hasRewardAmount = rewardAmount != null && rewardAmount !== "";
  const currentRewardCondition =
    currentStepObj?.rewardIndex != null
      ? activity?.rewards?.[currentStepObj.rewardIndex]?.conditions?.[currentStep - 1] ?? null
      : null;
  const conditionHeadlineTitle = currentRewardCondition?.title;
  const rewardHeadlineDisplay =
    conditionHeadlineTitle != null && String(conditionHeadlineTitle).trim() !== ""
      ? String(conditionHeadlineTitle).trim()
      : currentRewardCondition?.conditionDescription ?? "";
  const rewardHeadlinePopover = currentRewardCondition?.conditionDescription ?? "";
  const rewardProgressCurrent = Number.isFinite(Number(currentStepObj?.progressCurrent))
    ? Number(currentStepObj?.progressCurrent)
    : 0;
  const rewardProgressTarget = Number.isFinite(Number(currentStepObj?.progressTarget))
    ? Number(currentStepObj?.progressTarget)
    : 0;
  const rewardProgressPercent =
    rewardProgressTarget > 0
      ? Math.max(0, Math.min(100, (rewardProgressCurrent / rewardProgressTarget) * 100))
      : 0;
  const rewardProgressText =
    currentStepObj?.progressText ||
    (rewardProgressTarget > 0
      ? `${rewardProgressCurrent}/${rewardProgressTarget}${currentStepObj?.progressCurrency ? ` ${currentStepObj.progressCurrency}` : ""}`
      : "");

  const [countdown, setCountdown] = React.useState(null);
  const [countdownEnded, setCountdownEnded] = React.useState(false);
  const fallbackTimeText = activity?.timeRangeText ?? "";
  const isPermanent = activity?.permanent === true;

  React.useEffect(() => {
    if (isPermanent) {
      setCountdown(null);
      setCountdownEnded(false);
      return;
    }
    const endTime = activity?.endTime;
    if (endTime == null || typeof endTime !== "number") {
      setCountdown(null);
      setCountdownEnded(false);
      return;
    }
    const update = () => {
      const now = Date.now();
      const left = Math.max(0, endTime - now);
      if (left <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setCountdownEnded(true);
        return;
      }
      setCountdownEnded(false);
      const totalSeconds = Math.floor(left / 1000);
      const d = Math.floor(totalSeconds / (24 * 60 * 60));
      const h = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
      const m = Math.floor((totalSeconds % (60 * 60)) / 60);
      const s = totalSeconds % 60;
      setCountdown({ days: d, hours: h, minutes: m, seconds: s });
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [activity?.endTime, isPermanent]);

  const handleClaim = async () => {
    const id = claimRewardRecordId;
    if (claimLoading || claimed) return;
    if (id == null) {
      message.error(t("taskCenter.claimFailedContactSupport"));
      return;
    }
    setClaimLoading(true);
    try {
      await claimActivityReward(id);
      setClaimed(true);
      message.success(t("taskCenter.claimSuccess"));
      await fetchActivity();
    } catch (e) {
      const msg = e?.message || e?.mc || e?.msg;
      message.error(msg || t("taskCenter.claimFailed"));
    } finally {
      setClaimLoading(false);
    }
  };

  const fallbackJumpUrl = React.useMemo(() => {
    const rewards = activity?.rewards;
    if (!Array.isArray(rewards) || rewards.length === 0) return null;
    const conds = rewards[0]?.conditions;
    if (!Array.isArray(conds)) return null;
    const withUrl = conds.find((c) => getJumpUrl(c));
    return withUrl ? getJumpUrl(withUrl) : null;
  }, [activity]);

  const handleVerifyOrClaim = async () => {
    if (canClaim) {
      handleClaim();
      return;
    }
    const raw = currentStepObj?.jumpUrl || fallbackJumpUrl;
    if (!raw) return;
    if (depositNavigatingRef.current) return;
    const withActivity =
      currentStepObj?.depositAmountRule != null
        ? appendActivityDepositQueryToPath(raw, currentStepObj)
        : raw;
    depositNavigatingRef.current = true;
    try {
      const finalPath = await resolveActivityWebJumpPath(withActivity, userInfo, loginStatus);
      window.location.href = getUrl(finalPath);
    } catch (e) {
      depositNavigatingRef.current = false;
      window.location.href = getUrl(withActivity);
    }
  };

  const allRewardsClaimed =
    Array.isArray(activity?.rewards) &&
    activity.rewards.length > 0 &&
    activity.rewards.every((r) => r.status === "CLAIMED");
  const completedAndClaimed = activity?.userStatus === "COMPLETED" && allRewardsClaimed;

  if (loginStatus === 2) return null;

  if (!activity) return null;
  if (completedAndClaimed) return null;

  const showSteps = stepCount > 0;

  return (
    <div className={styles.wrap}>
      {showSteps ? (
        <div className={styles.steps}>
          <div className={styles.stepsLine}>
            {steps.map((_, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div className={`${styles.stepConnector} ${completedCount >= i ? styles.stepConnectorActive : ""}`} />
                )}
                <div className={`${styles.stepDot} ${completedCount > i ? styles.stepDotActive : styles.stepDotInactive}`}>
                  <div className={styles.dotInner} />
                  {completedCount >= stepCount && i === stepCount - 1 ? (
                    <button
                      type="button"
                      className={`${styles.stepGift} ${canClaim ? styles.stepGiftClickable : ""}`}
                      onClick={canClaim ? handleClaim : undefined}
                      disabled={!canClaim || claimLoading}
                      aria-label={canClaim ? t("taskCenter.claim") : undefined}
                    >
                      <img src={giftIcon} alt="" />
                    </button>
                  ) : (
                    <span className={styles.dotNum}>{i + 1}</span>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
          <div className={styles.stepsLabels}>
            {steps.map((s, i) => (
              <span
                key={i}
                className={`${styles.label} ${completedCount > i ? styles.labelActive : styles.labelInactive}`}
              >
                {s?.stepName ?? ""}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className={`${styles.card} ${styles.cardReward}`}>
        <div className={styles.cardTop}>
          {hasRewardAmount ? (
            <div className={styles.rewardHero}>
              <div className={styles.rewardHeroMain}>
                <div className={styles.rewardHeadline}>
                  <p className={styles.rewardHeadlineText}>{rewardHeadlineDisplay}</p>
                  {rewardHeadlinePopover ? (
                    <Popover
                      trigger="click"
                      placement="bottomLeft"
                      overlayClassName="darkInfoPopover"
                      content={<div className={styles.rewardHeadlinePopoverContent}>{rewardHeadlinePopover}</div>}
                    >
                      <span
                        className={styles.rewardHeadlineInfoIcon}
                        role="button"
                        tabIndex={0}
                        aria-label="Info"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                          }
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                            stroke="#939393"
                            strokeWidth="0.7"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M6.09272 3C6.66667 3 7.12583 3.15011 7.47903 3.46799C7.8234 3.77704 8 4.20088 8 4.73068C8 5.16336 7.88521 5.52539 7.67329 5.80795C7.59382 5.90508 7.34658 6.13466 6.92274 6.49669C6.7638 6.63797 6.64018 6.78808 6.56071 6.94702C6.46358 7.12362 6.41943 7.31788 6.41943 7.53863V7.68874H5.47461V7.53863C5.47461 7.21192 5.52759 6.92936 5.64238 6.69978C5.766 6.45254 6.10155 6.09051 6.64901 5.60486L6.79912 5.43709C6.95806 5.234 7.03753 5.02208 7.03753 4.79249C7.03753 4.48344 6.94923 4.24503 6.78146 4.06843C6.60486 3.89183 6.35762 3.80353 6.03974 3.80353C5.64238 3.80353 5.35099 3.92715 5.17439 4.17439C5.01545 4.38631 4.93598 4.69536 4.93598 5.08389H4C4 4.43929 4.18543 3.93598 4.56512 3.56512C4.93598 3.18543 5.44812 3 6.09272 3ZM5.94261 8.16556C6.12804 8.16556 6.28698 8.21854 6.4106 8.34216C6.53422 8.45695 6.59603 8.60706 6.59603 8.79249C6.59603 8.97793 6.53422 9.12804 6.4106 9.25166C6.27815 9.36645 6.11921 9.42826 5.94261 9.42826C5.75718 9.42826 5.60706 9.36645 5.49227 9.24283C5.35982 9.11921 5.29801 8.9691 5.29801 8.79249C5.29801 8.60706 5.35982 8.45695 5.49227 8.34216C5.60706 8.21854 5.75718 8.16556 5.94261 8.16556Z"
                            fill="#939393"
                          />
                        </svg>
                      </span>
                    </Popover>
                  ) : null}
                </div>
                <div className={`${styles.rewardAmount} ${styles.rewardAmountVisible}`}>
                  <span className={styles.amount}>{rewardAmount}</span>
                  {rewardCurrency ? <span className={styles.unit}>{rewardCurrency}</span> : null}
                </div>
              </div>
              <img className={styles.moneyImg} src={taskHeroImg} alt="" aria-hidden />
            </div>
          ) : (
            <div className={`${styles.rewardHero} ${styles.rewardHeroNoReward}`}>
              <div className={styles.rewardHeroMain}>
                <p className={styles.cardTitle}>
                  {currentStepObj?.stepName ?? activity?.activityName ?? activity?.name ?? ""}
                </p>
                <p className={styles.cardSubtitle}>
                  {((currentStepObj?.stepDescription ?? "") + " " + (currentStepObj?.progressText ?? "")).trim()}
                </p>
              </div>
              <img className={styles.moneyImg} src={taskHeroImg} alt="" aria-hidden />
            </div>
          )}
          {hasRewardAmount ? (
            <div className={styles.rewardProgressWrap}>
              <div className={styles.rewardProgressTrack}>
                <div
                  className={styles.rewardProgressFill}
                  style={{ width: `${rewardProgressPercent}%` }}
                />
              </div>
              <div className={styles.rewardProgressMeta}>
                <span className={styles.rewardProgressLabel}>{t("activity.progress") || "Progress"}</span>
                <span className={styles.rewardProgressValue}>{rewardProgressText}</span>
              </div>
            </div>
          ) : !isPermanent ? (
            <div className={styles.countdownRow}>
              <div className={styles.limitedTime}>
                <LimitedTimeIcon />
                <span className={styles.limitedText}>{t("taskCenter.limitedTime")}</span>
              </div>
              {countdownEnded ? (
                <span className={styles.countdownBoxes}>{t("taskCenter.ended")}</span>
              ) : countdown ? (
                <div className={styles.countdownBoxes}>
                  <span className={styles.countdownItem}>
                    <span className={styles.countdownNum}>
                      {countdown.days >= 99 ? "99+" : String(countdown.days).padStart(2, "0")}
                    </span>
                    <span className={styles.countdownUnit}>D</span>
                  </span>
                  <span className={styles.countdownColon}>:</span>
                  <span className={styles.countdownItem}>
                    <span className={styles.countdownNum}>{String(countdown.hours).padStart(2, "0")}</span>
                    <span className={styles.countdownUnit}>H</span>
                  </span>
                  <span className={styles.countdownColon}>:</span>
                  <span className={styles.countdownItem}>
                    <span className={styles.countdownNum}>{String(countdown.minutes).padStart(2, "0")}</span>
                    <span className={styles.countdownUnit}>M</span>
                  </span>
                  <span className={styles.countdownColon}>:</span>
                  <span className={styles.countdownItem}>
                    <span className={styles.countdownNum}>{String(countdown.seconds).padStart(2, "0")}</span>
                    <span className={styles.countdownUnit}>S</span>
                  </span>
                </div>
              ) : fallbackTimeText ? (
                <span className={styles.countdownBoxes}>{fallbackTimeText}</span>
              ) : null}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={canClaim ? handleClaim : handleVerifyOrClaim}
          disabled={canClaim && claimLoading}
        >
          {canClaim
            ? claimLoading
              ? t("taskCenter.claimLoading")
              : t("taskCenter.claim")
            : claimed || isCompleted
              ? t("taskCenter.completed")
              : showSteps
                ? currentStepObj?.buttonText ?? t("taskCenter.verifyNow")
                : t("taskCenter.verifyNow")}
        </button>
      </div>
    </div>
  );
}
